import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal } from '@amzn/alchemy-components-react';
import { AuthgardMfaStatus } from "src/common/enums";
import { MFA_STATUS_SUPPORT_URL } from "src/common/constants";
import { formatExpiryLabel, humanizeMfaStatus } from "src/common/util";
import { useManageScan2Auth } from "src/hooks/use-manage-scan2auth";
import type { MfaModalProvider } from "src/hooks/use-manage-scan2auth";
import { ConfirmableAction } from "src/components/modals/confirmable-action";

// Alchemy has no built-in destructive variant — use its error-alert color token
// to flag destructive actions (Revoke, Cancel Enrollment) so they're visually
// distinct from routine actions. Token defined in alchemy-components/styles/colors.css.
const destructiveButtonStyle: React.CSSProperties = {
    borderColor: 'var(--color-base-error-alert, #c00)',
    color: 'var(--color-base-error-alert, #c00)',
};

// Action buttons stack vertically with the primary action on top. `align-items-center` centers
// each (content-width) button on the modal's axis within a full-width flex column, so they line
// up with the text-center'd Refresh Status button below. Secondary/destructive buttons sit below
// with a top margin. (An earlier `align-items-stretch mx-auto` + max-width approach left the box
// off-center — auto-margin centering is unreliable on a flex container without an explicit width.)
const ACTION_STACK_CLASS = 'd-flex flex-column align-items-center';

export type { MfaModalProvider };

interface ManageScan2AuthModalProps {
    open: boolean
    setOpen: CallableFunction
    principalArn: string
    fullName: string
    alias: string
    modalTitle: string
    initialStatus: AuthgardMfaStatus
    // Raw ISO-8601 invite expiry from bulkGetMfaStatus, or null when there is no pending invite.
    // The hook parses it and derives expired-ness — no separate "expired" flag is threaded.
    initialExpiry?: string | null
    mfaProvider: MfaModalProvider
    // Scope (site) and userType are required by bulkExecuteAuthgardAdminOperation's target input.
    scope: string
    userType: string
}

/**
 * Modal for Authgard MFA management (Scan2Auth and FIDO2). All state comes from real-shaped data —
 * the initial status/expiry seed from bulkGetMfaStatus and every mutation from
 * bulkExecuteAuthgardAdminOperation — so nothing here is fabricated. During development those calls
 * are served by the dev-only mock Apollo link (see apollo-client.tsx); swapping to the live backend
 * is a link change with no edit to this component.
 *
 * States:
 *   NO_RECORD    — User has not been invited to enroll yet
 *   INITIATED    — Admin has generated a registration code (via AdminOperations INITIATE)
 *   ONBOARDED    — User has successfully registered their credentials
 *   REINITIATED  — A re-invite was issued for an in-progress enrollment (Authgard V2 folded
 *                  REINITIATE into INITIATE, and an INITIATE re-invite still yields this status)
 *   RESET_FIDO2  — Admin has revoked credentials (legacy V1-era status)
 *   REREGISTER   — Admin has revoked credentials and issued a re-registration code (via
 *                  AdminOperations REREGISTER, the V2 rename of RESET_MFA)
 *
 * The modal body is rendered by one method per state (renderNoRecord / renderInProgress /
 * renderOnboarded / renderReset), each taking only the arguments its state needs and composing a
 * shared layout frame (renderStateFrame). renderBody dispatches on the current status.
 *
 * REUSED unchanged for both providers: the Scan2Auth and FIDO2 flows are
 * identical — the only difference is the name of the auth method. That name is
 * carried by `credentialsLabel` (derived from `props.mfaProvider`) and the modal
 * title, and every user-facing string routes through it, so there is no
 * per-provider branching. Adding another Authgard auth method only needs its label.
 *
 * Note: the RESET_FIDO2 status is a backend-defined name that applies to both
 * providers — it is not FIDO2-only despite the naming.
 */
export const ManageScan2AuthModal = (props: ManageScan2AuthModalProps) => {

    const { t } = useTranslation();

    // All state + admin-operation orchestration lives in the hook; this component is presentational.
    const {
        currentStatus,
        expiryDate,
        regCode,
        errorMessage,
        submitting,
        refreshing,
        handleGenerateCode,
        handleRevealCode,
        cancelEnrollment,
        revoke,
        handleRefresh,
        resetState,
    } = useManageScan2Auth({
        open: props.open,
        principalArn: props.principalArn,
        scope: props.scope,
        userType: props.userType,
        mfaProvider: props.mfaProvider,
        initialStatus: props.initialStatus,
        initialExpiry: props.initialExpiry,
    });

    const credentialsLabel = props.mfaProvider === 'FIDO2' ? 'FIDO2' : 'Scan2Auth';

    // --- Shared render helpers (used by the per-state body methods) ---

    const renderHeaderDescription = (): React.ReactNode => (
        // Custom header subtitle via the Alchemy header-description slot: shows the unique
        // "Full Name (alias)" identity and lets us set the title↔name gap (marginTop) — the
        // attribute-based headerDescription exposes no spacing hook. Slotted content inherits
        // light-DOM color, so set the header color explicitly.
        <span
            slot="header-description"
            style={{ display: 'block', marginTop: '0.35rem', color: 'var(--modal-header-color, #ffffff)' }}
        >
            {props.fullName}{props.alias ? ` (${props.alias})` : ''}
        </span>
    );

    // Registration Code value: an em-dash when the state has no live code to retrieve; otherwise
    // the code once we hold it (models FETCH_REGCODE), masked until then. `hasRetrievableCode` is
    // supplied by the calling state (derived from its own expiry). We hold a code exactly when it
    // should be shown, so a present `regCode` is the reveal condition.
    const renderRegistrationCode = (hasRetrievableCode: boolean): React.ReactNode => {
        if (!hasRetrievableCode) {
            return <span className="ml-2 text-muted">—</span>;
        }
        if (regCode) {
            return (
                <span className="ml-2" style={{ fontSize: '1.25rem', fontFamily: 'monospace', letterSpacing: '0.1em' }}>
                    {regCode}
                </span>
            );
        }
        return (
            <>
                <span className="ml-2 text-muted" style={{ fontSize: '1.25rem', fontFamily: 'monospace', letterSpacing: '0.1em' }}>
                    ••••••••
                </span>
                {/* margin-left:auto pushes the button to the row's right edge. */}
                <Button
                    id="scan2auth-reveal-btn"
                    size="sm"
                    variant="base"
                    label={t('manage-credentials-reveal-code')}
                    onClick={handleRevealCode}
                    style={{ marginLeft: 'auto' }}
                />
            </>
        );
    };

    const renderError = (): React.ReactNode => {
        if (!errorMessage) {
            return null;
        }
        return (
            <div className="row mb-2">
                <div className="col">
                    <div style={{ background: '#fce4e4', border: '1px solid #dc3545', borderRadius: '4px', padding: '8px 12px' }}>
                        <Icon icon="warning" className="mr-1 c-error-alert" />
                        <strong>{t('error')}: </strong>
                        <span>{errorMessage}</span>
                    </div>
                </div>
            </div>
        );
    };

    // Shared body layout. Each per-state method fully configures this frame for its state; the
    // frame owns only the common skeleton (status header rows, error, guidance line, refresh,
    // actions slot). `showInviteRows` gates the expiry + registration-code rows (hidden for the
    // states with no invite). The frame derives "expired" directly from `expiryDate`; a state can
    // instead show the "Enrolled" text via `expiryEnrolledText`. Refresh + guidance are per state.
    interface StateFrameOptions {
        color: string;
        statusIcon?: string;
        description: string;
        showInviteRows: boolean;
        // The invite expiry date; the frame computes expired-ness (date < now) for its styling.
        expiryDate?: Date | null;
        // ONBOARDED has no invite date but shows an "Enrolled" label in the expiry row.
        expiryEnrolledText?: boolean;
        // Whether this state has a live code the admin can reveal — supplied per state (each derives
        // it from its own expiry). Drives the reg-code row's reveal affordance vs an em-dash.
        hasRetrievableCode?: boolean;
        guidance: string;
        showRefresh: boolean;
        actions: React.ReactNode;
    }

    const renderStateFrame = (opts: StateFrameOptions): React.ReactNode => {
        // The invite is expired iff its date is in the past — a pure presentation calc the UI
        // derives directly from the date it's handed (no separate isExpired flag needed).
        const expired = !!opts.expiryDate && opts.expiryDate < new Date();
        const expiryValue: React.ReactNode = opts.expiryEnrolledText
            ? <span className="text-muted">{t('manage-credentials-expiry-enrolled')}</span>
            : !opts.expiryDate
                ? <span className="text-muted">—</span>
                : expired
                    ? (
                        <span style={{ color: '#dc3545' }}>
                            <Icon icon="warning" className="mr-1" />
                            {formatExpiryLabel(t, opts.expiryDate)}
                        </span>
                    )
                    : <span>{formatExpiryLabel(t, opts.expiryDate)}</span>;
        return (
            <>
                <div className="mb-3">
                    {/* Status row */}
                    <div className="row mb-2">
                        <div className="col d-flex align-items-center">
                            <strong>{t('status')}: </strong>
                            <span className="ml-1" style={{ color: opts.color, fontWeight: 600 }}>{humanizeMfaStatus(t, currentStatus)}</span>
                            {opts.statusIcon && (
                                <Icon icon={opts.statusIcon} className="ml-1 c-success-alert" />
                            )}
                        </div>
                    </div>

                    {/* Status description */}
                    <div className="row mb-2">
                        <div className="col">
                            <span className="text-muted" style={{ fontSize: '0.85rem' }}>{opts.description}</span>
                        </div>
                    </div>

                    {/* Invite Expiry + Registration Code — only for states that have an invite. */}
                    {opts.showInviteRows && (
                        <>
                            <div className="row mb-2">
                                <div className="col">
                                    <strong>{t('manage-credentials-invite-expiry-label')}: </strong>
                                    {expiryValue}
                                </div>
                            </div>
                            <div className="row mb-2">
                                {/* Label + masked bullets sit left; the reveal button is pushed to
                                    the right edge (margin-left:auto on its wrapper, inside
                                    renderRegistrationCode). */}
                                <div className="col d-flex align-items-center">
                                    <strong>{t('manage-credentials-registration-code-label')}: </strong>
                                    {renderRegistrationCode(!!opts.hasRetrievableCode)}
                                </div>
                            </div>
                            {/* Rotate-on-fetch warning (Authgard V2): revealing issues a fresh code
                                and invalidates any previously shared one. Only meaningful while a
                                retrievable code is still masked — once revealed, the code shown IS
                                the live one. */}
                            {!!opts.hasRetrievableCode && !regCode && (
                                <div className="row mb-2">
                                    <div className="col">
                                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                                            {t('manage-credentials-reveal-rotates')}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {renderError()}
                <hr className="my-2" />

                {/* Guidance sits directly above the buttons. */}
                <p className="text-muted text-center mt-3 mb-2" style={{ fontSize: '0.85rem' }}>
                    {opts.guidance}
                </p>

                {/* Refresh Status on top (the expected next step); the enrollment actions render below. */}
                {opts.showRefresh && (
                    <div className="text-center mb-2">
                        <Button
                            id="scan2auth-refresh-btn"
                            icon="refresh"
                            iconPosition="left"
                            label={refreshing ? t('manage-credentials-refreshing') : t('manage-credentials-refresh-status')}
                            size="sm"
                            variant="base"
                            disabled={refreshing || submitting}
                            onClick={handleRefresh}
                        />
                    </div>
                )}

                <div className="mt-2">
                    {opts.actions}
                </div>
            </>
        );
    };

    // --- One body method per state ---

    // NO_RECORD: not invited yet. No invite rows; the only action is to generate the first code.
    const renderNoRecord = (): React.ReactNode => renderStateFrame({
        color: '#6c757d',
        description: t('manage-credentials-desc-not-invited'),
        showInviteRows: false,
        guidance: t('manage-credentials-info-not-invited'),
        showRefresh: false,
        actions: (
            <div className={ACTION_STACK_CLASS}>
                <Button
                    id="scan2auth-generate-btn"
                    size="lg"
                    label={t('manage-credentials-generate-code')}
                    onClick={handleGenerateCode}
                />
            </div>
        ),
    });

    // INITIATED / REINITIATED: an active enrollment. When valid, the code is shown above and only
    // Cancel is offered (with the "Next" guidance + Refresh); when expired, guidance flips to the
    // expired hint and a "Generate a new code" button appears. A cancel-confirm overlays the actions.
    const renderInProgress = (args: {
        status: AuthgardMfaStatus.INITIATED | AuthgardMfaStatus.REINITIATED;
        expiryDate: Date | null;
    }): React.ReactNode => {
        const expired = !!args.expiryDate && args.expiryDate < new Date();
        return renderStateFrame({
            color: '#0d6efd',
            description: args.status === AuthgardMfaStatus.INITIATED
                ? t('manage-credentials-desc-code-generated')
                : t('manage-credentials-desc-code-reissued'),
            showInviteRows: true,
            expiryDate: args.expiryDate,
            hasRetrievableCode: !expired, // a live invite has a code to reveal; an expired one doesn't
            guidance: expired
                ? t('manage-credentials-info-active-expired')
                : t('manage-credentials-enrollment-next', { credentialType: credentialsLabel }),
            showRefresh: !expired,
            // ConfirmableAction owns the cancel-confirm toggle; keyed on `open` so a reopened modal
            // never shows a stale prompt. Its dialog replaces this whole actions slot when confirming.
            actions: (
                <ConfirmableAction
                    key={`cancel-${props.open}`}
                    title={t('manage-credentials-confirm-cancel-title', { name: props.fullName })}
                    body={t('manage-credentials-confirm-cancel-body')}
                    confirmLabel={t('manage-credentials-confirm-cancel-yes')}
                    keepLabel={t('manage-credentials-confirm-cancel-keep')}
                    confirmId="scan2auth-confirm-cancel-btn"
                    keepId="scan2auth-abort-cancel-btn"
                    submitting={submitting}
                    onConfirm={cancelEnrollment}
                >
                    {(requestConfirm) => (
                        <div className={ACTION_STACK_CLASS}>
                            {expired && (
                                <Button
                                    id="scan2auth-generate-btn"
                                    size="lg"
                                    label={t('manage-credentials-generate-new-code')}
                                    onClick={handleGenerateCode}
                                />
                            )}
                            <Button
                                id="scan2auth-cancel-btn"
                                className={expired ? "mt-2" : ""}
                                size="lg"
                                label={t('manage-credentials-cancel-enrollment')}
                                variant="base"
                                style={destructiveButtonStyle}
                                onClick={requestConfirm}
                            />
                        </div>
                    )}
                </ConfirmableAction>
            ),
        });
    };

    // ONBOARDED: enrolled. Shows the "enrolled" expiry text and no retrievable code; the only
    // action is to revoke credentials. A revoke-confirm overlays the actions.
    const renderOnboarded = (): React.ReactNode => renderStateFrame({
        color: '#198754',
        statusIcon: 'checkSolid',
        description: t('manage-credentials-desc-enrolled', { credentialType: credentialsLabel }),
        showInviteRows: true,
        expiryEnrolledText: true,
        hasRetrievableCode: false, // enrolled: no pending code to reveal
        guidance: t('manage-credentials-info-enrolled', { credentialType: credentialsLabel }),
        showRefresh: false,
        actions: (
            <ConfirmableAction
                key={`revoke-${props.open}`}
                title={t('manage-credentials-confirm-revoke-title', { credentialType: credentialsLabel, name: props.fullName })}
                body={t('manage-credentials-confirm-revoke-body')}
                confirmLabel={t('manage-credentials-confirm-revoke-yes', { credentialType: credentialsLabel })}
                keepLabel={t('manage-credentials-confirm-revoke-keep')}
                confirmId="scan2auth-confirm-revoke-btn"
                keepId="scan2auth-cancel-revoke-btn"
                submitting={submitting}
                onConfirm={revoke}
            >
                {(requestConfirm) => (
                    <div className={ACTION_STACK_CLASS}>
                        <Button
                            id="scan2auth-revoke-btn"
                            size="lg"
                            label={t('manage-credentials-revoke', { credentialType: credentialsLabel })}
                            variant="base"
                            style={destructiveButtonStyle}
                            onClick={requestConfirm}
                        />
                    </div>
                )}
            </ConfirmableAction>
        ),
    });

    // RESET_FIDO2: credentials revoked. Re-initiate only when expired; when still valid the code is
    // shown in the header and Refresh sits above, so there's no action. No confirm reaches this state.
    const renderReset = (args: { expiryDate: Date | null }): React.ReactNode => {
        const expired = !!args.expiryDate && args.expiryDate < new Date();
        return renderStateFrame({
            color: '#fd7e14',
            description: t('manage-credentials-desc-revoked'),
            showInviteRows: true,
            expiryDate: args.expiryDate,
            hasRetrievableCode: !expired, // a live invite has a code to reveal; an expired one doesn't
            guidance: expired
                ? t('manage-credentials-info-reset-expired')
                : t('manage-credentials-enrollment-next', { credentialType: credentialsLabel }),
            showRefresh: !expired,
            actions: expired
                ? (
                    <div className={ACTION_STACK_CLASS}>
                        <Button
                            id="scan2auth-generate-btn"
                            size="lg"
                            label={t('manage-credentials-generate-new-code')}
                            onClick={handleGenerateCode}
                        />
                    </div>
                )
                : null,
        });
    };

    // UNKNOWN: the status couldn't be loaded (a failed read, or a per-principal lookup error). We
    // don't know the real state, so we offer no state-changing actions — only Refresh and a pointer
    // to the team if it persists.
    const renderUnavailable = (): React.ReactNode => (
        <div className="mb-3">
            <div className="row mb-2">
                <div className="col d-flex align-items-center">
                    <Icon icon="warning" className="mr-1 c-error-alert" />
                    <strong>{t('manage-credentials-status-unavailable-title')}</strong>
                </div>
            </div>
            <div className="row mb-2">
                <div className="col">
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                        {t('manage-credentials-status-unavailable-body')}{' '}
                        <a href={MFA_STATUS_SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                            {t('manage-credentials-status-unavailable-contact')}
                        </a>
                    </span>
                </div>
            </div>
            <hr className="my-2" />
            <div className="text-center mb-2">
                <Button
                    id="scan2auth-refresh-btn"
                    icon="refresh"
                    iconPosition="left"
                    label={refreshing ? t('manage-credentials-refreshing') : t('manage-credentials-refresh-status')}
                    size="sm"
                    variant="base"
                    disabled={refreshing || submitting}
                    onClick={handleRefresh}
                />
            </div>
        </div>
    );

    const renderBody = (): React.ReactNode => {
        switch (currentStatus) {
            case AuthgardMfaStatus.NO_RECORD:
                return renderNoRecord();
            case AuthgardMfaStatus.INITIATED:
            case AuthgardMfaStatus.REINITIATED:
                return renderInProgress({ status: currentStatus, expiryDate });
            case AuthgardMfaStatus.ONBOARDED:
                return renderOnboarded();
            case AuthgardMfaStatus.RESET_FIDO2:
            case AuthgardMfaStatus.REREGISTER:
                return renderReset({ expiryDate });
            case AuthgardMfaStatus.UNKNOWN:
                return renderUnavailable();
            default:
                return null;
        }
    };

    return (
        <Modal
            id="manage-scan2auth-modal"
            className="mb-1"
            header={props.modalTitle}
            open={props.open}
            onOpen={() => {}}
            onClose={(e: Event) => {
                // Alchemy's <Modal> dispatches a bubbling, composed `close` CustomEvent on the host
                // for every real dismissal (X button, overlay click, ESC, programmatic open=false —
                // all funnel through its `open` setter). Because the event bubbles and crosses shadow
                // boundaries, a nested dismissible Alchemy component could surface its own `close`
                // too, so only treat it as a real close when it originated AT the modal host itself.
                if (e.target === e.currentTarget) {
                    resetState();
                    props.setOpen(false);
                }
            }}
        >
            {renderHeaderDescription()}
            {/* No stopPropagation here: the modal's own outside-click handler already ignores clicks
                whose composedPath includes the content, so inner clicks never dismiss it — and
                swallowing bubbling would break nested components that rely on document-level
                outside-click listeners (dropdowns, popovers). */}
            <div className="container-fluid">
                {renderBody()}
            </div>
        </Modal>
    );
};
